import { useEffect, useRef, useState } from "react";
import { FaSyringe } from "react-icons/fa";
import { FiAlertCircle, FiFilter } from "react-icons/fi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioSanidadePecuaria = () => {
  const [dados, setDados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  const [tiposManejo, setTiposManejo] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    animais: useRef(null),
    lotes: useRef(null),
    fazendas: useRef(null),
    tiposManejo: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    animais: [],
    lotes: [],
    fazendas: [],
    tiposManejo: [],
    data_inicio: "",
    data_fim: "",
    tipo_consulta: "animal" // "animal" ou "lote"
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [animaisRes, lotesRes, fazendasRes, tiposManejoRes] = await Promise.all([
        listarTudo("/pecuaria/animal/"),
        api.get("/pecuaria/lotes/"),
        api.get("/fazendas/fazendas/"),
        listarTudo("/pecuaria/tipos_manejo/")
      ]);

      setAnimais(Array.isArray(animaisRes.data) ? animaisRes.data : animaisRes.data.results || []);
      setLotes(lotesRes.data.results || []);
      setFazendas(fazendasRes.data.results || []);
      setTiposManejo(Array.isArray(tiposManejoRes.data) ? tiposManejoRes.data : tiposManejoRes.data.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setAnimais([]);
      setLotes([]);
      setFazendas([]);
      setTiposManejo([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {
        tipo_consulta: filtros.tipo_consulta
      };

      if (filtros.animais && filtros.animais.length > 0) {
        params.animais = filtros.animais.join(",");
      }
      if (filtros.lotes && filtros.lotes.length > 0) {
        params.lotes = filtros.lotes.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.tiposManejo && filtros.tiposManejo.length > 0) {
        params.tipos_manejo = filtros.tiposManejo.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }

      console.log("🌐 URL:", `/relatorios/pecuarios/sanidade/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/pecuarios/sanidade/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      animais: [],
      lotes: [],
      fazendas: [],
      tiposManejo: [],
      data_inicio: "",
      data_fim: "",
      tipo_consulta: "animal"
    });
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Sanidade Pecuaria"
    );

    XLSX.writeFile(workbook, "relatorio_sanidade_pecuaria.xlsx");
  };

  const exportarPDF = async () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const colunas = [
      { key: "identificacao", label: "Identificação" },
      { key: "nome", label: "Nome" },
      { key: "lote", label: "Lote" },
      { key: "medicamento", label: "Vacina/Medicamento" },
      { key: "data_aplicacao", label: "Data Aplicação" },
      { key: "dose", label: "Dose" },
      { key: "unidade", label: "Unidade" },
      { key: "responsavel", label: "Responsável" },
      { key: "carencia_dias", label: "Carência (dias)" },
      { key: "proxima_aplicacao", label: "Próxima Aplicação" },
      { key: "observacoes", label: "Observações" },
    ];

    await exportarRelatorioPDF({
      titulo: "Relatório de Sanidade Pecuária",
      colunas,
      dados,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor) => (valor === null || valor === undefined || valor === "" ? "-" : String(valor)),
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Sanidade Pecuaria</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .carencia-alta { color: red; font-weight: bold; }
            .carencia-media { color: orange; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Sanidade Pecuaria</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Tipo de consulta:</strong> ${filtros.tipo_consulta === 'animal' ? 'Por Animal' : 'Por Lote'}</p>
            <p><strong>Total de registros:</strong> ${dados.length}</p>
          </div>

          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Periodo analisado:</strong>
            <p>${filtros.data_inicio || 'Sem data inicial'} a ${filtros.data_fim || 'Sem data final'}</p>
          </div>
          ` : ''}

          <table>
            <thead>
              <tr>
                <th>IDENTIFICACAO</th>
                <th>NOME</th>
                <th>LOTE</th>
                <th>VACINA/MEDICAMENTO</th>
                <th>DATA APLICACAO</th>
                <th>DOSE</th>
                <th>UNIDADE</th>
                <th>RESPONSAVEL</th>
                <th>CARENCIA</th>
                <th>PROXIMA APLICACAO</th>
                <th>OBSERVACOES</th>
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => {
                const carencia = parseInt(item.carencia_dias);
                let carenciaClass = '';
                if (carencia > 30) carenciaClass = 'carencia-alta';
                else if (carencia > 15) carenciaClass = 'carencia-media';

                return `
                <tr>
                  <td>${item.identificacao || '-'}</td>
                  <td>${item.nome || '-'}</td>
                  <td>${item.lote || '-'}</td>
                  <td>${item.medicamento || '-'}</td>
                  <td>${item.data_aplicacao || '-'}</td>
                  <td>${item.dose || '-'}</td>
                  <td>${item.unidade || '-'}</td>
                  <td>${item.responsavel || '-'}</td>
                  <td class="${carenciaClass}">${item.carencia_dias || '-'} dias</td>
                  <td>${item.proxima_aplicacao || '-'}</td>
                  <td>${item.observacoes || '-'}</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp, disabled = false }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome || s.numero_identificacao).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => !disabled && setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {!disabled && openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome || option.numero_identificacao || option.nome_codigo}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={FaSyringe}
            titulo="Relatório de Sanidade Pecuária"
            descricao="Consulte o histórico de vacinas e medicamentos aplicados nos animais"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipo de Consulta */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Consulta
                </label>
                <select
                  name="tipo_consulta"
                  value={filtros.tipo_consulta}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="animal">Por Animal</option>
                  <option value="lote">Por Lote</option>
                </select>
              </div>

              {/* Tipos de Manejo/Vacinas - Multi Select */}
              <MultiSelect
                label="Vacinas/Medicamentos"
                options={tiposManejo}
                selectedValues={filtros.tiposManejo}
                fieldName="tiposManejo"
                refProp={dropdownRefs.tiposManejo}
              />

              {/* Animais - Multi Select (só aparece se for por animal) */}
              {filtros.tipo_consulta === "animal" && (
                <MultiSelect
                  label="Animais"
                  options={animais}
                  selectedValues={filtros.animais}
                  fieldName="animais"
                  refProp={dropdownRefs.animais}
                />
              )}

              {/* Lotes - Multi Select */}
              <MultiSelect
                label="Lotes"
                options={lotes}
                selectedValues={filtros.lotes}
                fieldName="lotes"
                refProp={dropdownRefs.lotes}
              />

              {/* Fazendas - Multi Select */}
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.animais && filtros.animais.length > 0) ||
              (filtros.lotes && filtros.lotes.length > 0) ||
              (filtros.fazendas && filtros.fazendas.length > 0) ||
              (filtros.tiposManejo && filtros.tiposManejo.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.animais && filtros.animais.map((id) => {
                    const animal = animais.find(a => a.id === id);
                    return animal ? (
                      <span key={`animal-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Animal: {animal.numero_identificacao}
                        <button
                          onClick={() => handleMultiSelect("animais", id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.lotes && filtros.lotes.map((id) => {
                    const lote = lotes.find(l => l.id === id);
                    return lote ? (
                      <span key={`lote-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Lote: {lote.nome}
                        <button
                          onClick={() => handleMultiSelect("lotes", id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.fazendas && filtros.fazendas.map((id) => {
                    const fazenda = fazendas.find(f => f.id === id);
                    return fazenda ? (
                      <span key={`fazenda-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Fazenda: {fazenda.nome}
                        <button
                          onClick={() => handleMultiSelect("fazendas", id)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.tiposManejo && filtros.tiposManejo.map((id) => {
                    const tipo = tiposManejo.find(t => t.id === id);
                    return tipo ? (
                      <span key={`tipo-${id}`} className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Medicamento: {tipo.nome}
                        <button
                          onClick={() => handleMultiSelect("tiposManejo", id)}
                          className="ml-1 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FaSyringe size={20} className="text-brand-green" />
                  Relatorio de Sanidade Pecuaria
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Tabela */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-800 font-medium">Total de registros:</span>
                  <span className="text-emerald-900 font-bold text-lg">{dados.length}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-emerald-800">Tipo:</span>
                  <span className="text-emerald-900">{filtros.tipo_consulta === 'animal' ? 'Por Animal' : 'Por Lote'}</span>
                </div>
              </div>

              {dados.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200" style={{ maxWidth: '100%' }}>
                  <div style={{ minWidth: 'max-content' }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          {filtros.tipo_consulta === 'animal' && (
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Identificação</th>
                          )}
                          {filtros.tipo_consulta === 'animal' && (
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Nome</th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Lote</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Vacina/Medicamento</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Data Aplicação</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Dose</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Unidade</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Carência</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Próxima Aplicação</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Observações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.map((item, index) => {
                          const carencia = parseInt(item.carencia_dias);
                          let carenciaClass = '';
                          if (carencia > 30) carenciaClass = 'text-red-600 font-bold';
                          else if (carencia > 15) carenciaClass = 'text-orange-600';

                          return (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              {filtros.tipo_consulta === 'animal' && (
                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.identificacao || "-"}</td>
                              )}
                              {filtros.tipo_consulta === 'animal' && (
                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.nome || "-"}</td>
                              )}
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.lote || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.medicamento || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_aplicacao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.dose || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.unidade || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.responsavel || "-"}</td>
                              <td className={`px-4 py-3 text-sm whitespace-nowrap ${carenciaClass}`}>
                                {item.carencia_dias ? `${item.carencia_dias} dias` : "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.proxima_aplicacao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.observacoes || "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioSanidadePecuaria;
